import { clearStoredCoachSession, getAuthToken } from "./auth";

const DEFAULT_TIMEOUT_MS = Number(process.env.REACT_APP_API_TIMEOUT_MS || 15000);
const PROGRAM_DRAFT_GENERATION_TIMEOUT_MS = 210000;
const DEFAULT_CACHE_TTL_MS = 12000;

const configuredApiBase = String(process.env.REACT_APP_API_BASE_URL || "").trim();
if (!configuredApiBase) {
  throw new Error(
    "[config] REACT_APP_API_BASE_URL is required; no API environment is selected"
  );
}

let parsedApiBase;
try {
  parsedApiBase = new URL(configuredApiBase);
} catch {
  throw new Error("[config] REACT_APP_API_BASE_URL must be an absolute URL");
}
if (!["http:", "https:"].includes(parsedApiBase.protocol)) {
  throw new Error("[config] REACT_APP_API_BASE_URL must use HTTP or HTTPS");
}

const API_BASE = configuredApiBase.replace(/\/+$/, "");

const responseCache = new Map();
const inflightGetRequests = new Map();

const isAbsoluteUrl = (value) => /^https?:\/\//i.test(String(value || ""));

const buildAuthHeaders = (headers) => {
  const nextHeaders = new Headers(headers || {});
  const token = getAuthToken();
  if (token && !nextHeaders.has("Authorization")) {
    nextHeaders.set("Authorization", `Bearer ${token}`);
  }
  return nextHeaders;
};

const buildSearchParams = (query) => {
  if (!query) return "";
  if (query instanceof URLSearchParams) {
    const serialized = query.toString();
    return serialized ? `?${serialized}` : "";
  }

  const params = new URLSearchParams();
  Object.entries(query).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;
    params.set(key, String(value));
  });
  const serialized = params.toString();
  return serialized ? `?${serialized}` : "";
};

export const buildApiUrl = (path, query) => {
  if (isAbsoluteUrl(path)) return `${path}${buildSearchParams(query)}`;
  const normalizedPath = String(path || "").startsWith("/")
    ? path
    : `/${String(path || "")}`;
  return `${API_BASE}${normalizedPath}${buildSearchParams(query)}`;
};

const parseResponseBody = async (response) => {
  if (response.status === 204) return null;
  const contentType = response.headers.get("content-type") || "";
  if (contentType.includes("application/json")) {
    return response.json();
  }
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
};

const stringifyForDebug = (value) => {
  if (value === undefined) return "undefined";
  if (value === null) return "null";
  if (typeof value === "string") return value;
  if (value instanceof FormData) {
    return JSON.stringify(Array.from(value.entries()));
  }
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
};

const buildApiError = async (response, url) => {
  let payload = null;
  try {
    payload = await parseResponseBody(response);
  } catch {
    payload = null;
  }

  const message =
    (payload && typeof payload === "object" && payload.message) ||
    (payload && typeof payload === "object" && payload.detail) ||
    `Erreur HTTP ${response.status}`;
  const error = new Error(message);
  error.status = response.status;
  error.payload = payload;
  error.url = url;
  return error;
};

const attachAbortListener = (controller, externalSignal) => {
  if (!externalSignal) return () => {};
  if (externalSignal.aborted) {
    controller.abort(externalSignal.reason);
    return () => {};
  }

  const onAbort = () => controller.abort(externalSignal.reason);
  externalSignal.addEventListener("abort", onAbort, { once: true });
  return () => externalSignal.removeEventListener("abort", onAbort);
};

export const apiRequest = async (path, options = {}) => {
  const {
    method = "GET",
    headers = {},
    body,
    query,
    signal,
    timeoutMs = DEFAULT_TIMEOUT_MS,
    cacheTtlMs = method.toUpperCase() === "GET" ? DEFAULT_CACHE_TTL_MS : 0,
    force = false,
  } = options;

  const upperMethod = method.toUpperCase();
  const url = buildApiUrl(path, query);
  const isGet = upperMethod === "GET";
  const cacheKey = `${upperMethod}:${url}`;

  if (isGet && !force && cacheTtlMs > 0) {
    const cached = responseCache.get(cacheKey);
    if (cached && cached.expiresAt > Date.now()) {
      return cached.data;
    }
    if (cached) {
      responseCache.delete(cacheKey);
    }
  }

  if (isGet && inflightGetRequests.has(cacheKey)) {
    return inflightGetRequests.get(cacheKey);
  }

  const controller = new AbortController();
  const removeAbortListener = attachAbortListener(controller, signal);
  const timeoutId = window.setTimeout(() => {
    controller.abort("request-timeout");
  }, timeoutMs);

  const hasJsonBody =
    body !== undefined && body !== null && typeof body === "object" && !(body instanceof FormData);
  const requestHeaders = buildAuthHeaders({
    Accept: "application/json",
    ...headers,
  });
  if (hasJsonBody) {
    requestHeaders.set(
      "Content-Type",
      requestHeaders.get("Content-Type") || "application/json"
    );
  }

  const fetchPromise = fetch(url, {
    method: upperMethod,
    headers: requestHeaders,
    signal: controller.signal,
    body:
      body === undefined
        ? undefined
        : hasJsonBody
          ? JSON.stringify(body)
          : body,
  })
    .then(async (response) => {
      if (response.status === 401) {
        clearStoredCoachSession();
      }
      if (!response.ok) {
        let debugPayload = null;
        try {
          debugPayload = await parseResponseBody(response.clone());
        } catch {
          debugPayload = null;
        }
        console.groupCollapsed(
          `[API ${upperMethod}] ${response.status} ${url}`
        );
        console.error("Request URL:", url);
        console.error("Request Method:", upperMethod);
        console.error("Request Headers:", Object.fromEntries(requestHeaders.entries()));
        console.error(
          "Request Payload:",
          body === undefined ? "undefined" : stringifyForDebug(body)
        );
        console.error("Response:", debugPayload);
        console.groupEnd();
        throw await buildApiError(response, url);
      }
      const data = await parseResponseBody(response);
      if (isGet && cacheTtlMs > 0) {
        responseCache.set(cacheKey, {
          data,
          expiresAt: Date.now() + cacheTtlMs,
        });
      } else if (!isGet) {
        const parsed = new URL(url, window.location.origin);
        invalidateApiCache((cachedUrl) => cachedUrl.includes(parsed.pathname));
      }
      return data;
    })
    .finally(() => {
      window.clearTimeout(timeoutId);
      removeAbortListener();
      if (isGet) {
        inflightGetRequests.delete(cacheKey);
      }
    });

  if (isGet) {
    inflightGetRequests.set(cacheKey, fetchPromise);
  }

  return fetchPromise;
};

export const apiGet = (path, options = {}) =>
  apiRequest(path, { ...options, method: "GET" });

export const apiFetch = (path, options = {}) => apiRequest(path, options);

export const prefetchGet = (path, options = {}) =>
  apiGet(path, options).catch(() => null);

export const invalidateApiCache = (matcher) => {
  if (!matcher) {
    responseCache.clear();
    return;
  }

  for (const key of responseCache.keys()) {
    const separatorIndex = key.indexOf(":");
    const url = separatorIndex === -1 ? key : key.slice(separatorIndex + 1);
    const shouldDelete =
      typeof matcher === "string"
        ? url.includes(matcher)
        : matcher instanceof RegExp
          ? matcher.test(url)
          : typeof matcher === "function"
            ? matcher(url)
            : false;
    if (shouldDelete) {
      responseCache.delete(key);
    }
  }
};

export const getProgrammingProfile = (options = {}) =>
  apiGet("/coach/programming-profile", {
    cacheTtlMs: 0,
    ...options,
  });

export const updateProgrammingProfile = (payload, options = {}) =>
  apiFetch("/coach/programming-profile", {
    ...options,
    method: "PUT",
    body: payload,
    cacheTtlMs: 0,
  });

export const getProgrammingAssistantProviders = (options = {}) =>
  apiGet("/coach/programming-assistant/providers", {
    cacheTtlMs: 0,
    ...options,
  });

export const generateProgramDraft = (payload, options = {}) =>
  apiFetch("/programs/generate-draft", {
    ...options,
    method: "POST",
    body: payload,
    cacheTtlMs: 0,
    timeoutMs: options.timeoutMs ?? PROGRAM_DRAFT_GENERATION_TIMEOUT_MS,
  });

export const getProgramDraft = (id, options = {}) =>
  apiGet(`/program-drafts/${encodeURIComponent(id)}`, {
    cacheTtlMs: 0,
    ...options,
  });

export const updateProgramDraft = (id, payload, options = {}) =>
  apiFetch(`/program-drafts/${encodeURIComponent(id)}`, {
    ...options,
    method: "PUT",
    body: payload,
    cacheTtlMs: 0,
  });

export const commitProgramDraft = (id, payload, options = {}) =>
  apiFetch(`/program-drafts/${encodeURIComponent(id)}/commit`, {
    ...options,
    method: "POST",
    body: payload,
    cacheTtlMs: 0,
  });

export const getApiBase = () => API_BASE;

let fetchShimInstalled = false;

export const installApiFetchAuthShim = () => {
  if (fetchShimInstalled || typeof window === "undefined" || typeof window.fetch !== "function") {
    return;
  }

  const nativeFetch = window.fetch.bind(window);
  window.fetch = (input, init = {}) => {
    const requestUrl =
      typeof input === "string"
        ? input
        : input instanceof Request
          ? input.url
          : String(input || "");

    const shouldInject =
      Boolean(requestUrl) &&
      isAbsoluteUrl(requestUrl) &&
      requestUrl.startsWith(API_BASE);

    if (!shouldInject) {
      return nativeFetch(input, init);
    }

    const headers = buildAuthHeaders(init.headers);
    return nativeFetch(input, {
      ...init,
      headers,
    });
  };

  fetchShimInstalled = true;
};
